import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { setPosthogConsent } from '@/lib/posthog';

export default function CookieConsent() {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('uta-cookie-consent');
        if (!consent) {
            // Show after a small delay
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem('uta-cookie-consent', 'accepted');
        setPosthogConsent(true);
        setIsVisible(false);
    };

    const handleDecline = () => {
        localStorage.setItem('uta-cookie-consent', 'declined');
        setPosthogConsent(false);
        setIsVisible(false);
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 z-[100] max-w-md w-full"
                >
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 relative overflow-hidden">
                        {/* Decorative accent */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-accent-500"></div>

                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-primary-50 rounded-full text-primary-600 mt-1">
                                <Cookie size={24} />
                            </div>

                            <div className="flex-1">
                                <h3 className="font-bold text-primary-900 mb-2">Cookies & Confidentialité</h3>
                                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                    Nous utilisons des cookies essentiels pour assurer le bon fonctionnement du site et sa sécurité.
                                    {import.meta.env.VITE_POSTHOG_KEY && (
                                        <> Si vous acceptez, nous mesurons l’audience des pages publiques (outil PostHog, sans zone admin). </>
                                    )}{' '}
                                    Pour en savoir plus, consultez notre <Link to="/privacy" className="text-accent-600 hover:underline font-medium">politique de confidentialité</Link>.
                                </p>

                                <div className="flex gap-3">
                                    <button
                                        onClick={handleAccept}
                                        className="flex-1 bg-primary-900 hover:bg-primary-800 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Check size={16} /> Accepter
                                    </button>
                                    <button
                                        onClick={handleDecline}
                                        className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Refuser
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={handleDecline}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Fermer"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
